import React from 'react';
import { Box, Card, Divider, Grid, LinearProgress, Stack, Typography, alpha } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import SouthEastIcon from '@mui/icons-material/SouthEast';

const COLORS = {
  navy: '#2B1B54',
  teal: '#0097A7',
  green: '#4CAF50',
  orange: '#FF9800',
  border: '#E5E5E5',
  cardBg: '#F7F7F4',
  gray: '#8A8A8A',
  darkText: '#1A1A1A',
  logoOrange: '#E86B1A',
};

const SERIF_FONT = "'Georgia', 'Times New Roman', serif";

const TREND_ICON = {
  up: NorthEastIcon,
  down: SouthEastIcon,
  neutral: ArrowForwardIcon,
};

const TREND_COLOR = {
  up: COLORS.green,
  down: COLORS.orange,
  neutral: COLORS.teal,
};

const SCORE_MIN = 300;
const SCORE_MAX = 900;
const SCORE_AXIS_LABELS = [300, 520, 620, 720, 820, 900];

const OverlineLabel = ({ children, sx, ...rest }) => (
  <Typography
    variant="overline"
    sx={{
      display: 'block',
      color: COLORS.gray,
      letterSpacing: 1.4,
      fontWeight: 600,
      fontSize: '11px',
      lineHeight: 1.6,
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Typography>
);

const ReportHeader = ({ report }) => {
  if (!report) return null;

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      spacing={1.5}
      sx={{ mb: 4, pb: 2.5, borderBottom: '1px solid', borderColor: COLORS.border }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <Box
          sx={{
            width: 0,
            height: 0,
            borderLeft: '7px solid transparent',
            borderRight: '7px solid transparent',
            borderBottom: `12px solid ${COLORS.logoOrange}`,
          }}
        />
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '15px', color: COLORS.darkText, lineHeight: 1.2 }}>
            Marmin AI
          </Typography>
          <Typography
            sx={{
              fontSize: '9px',
              letterSpacing: 1.2,
              color: COLORS.gray,
              textTransform: 'uppercase',
              fontWeight: 600,
            }}
          >
            Invoice Credit Intelligence
          </Typography>
        </Box>
      </Stack>

      <Typography sx={{ fontSize: '12px', color: COLORS.gray, fontFamily: 'monospace' }}>
        MICI &mdash; REPORT {report.id}
      </Typography>

      <Stack direction="row" spacing={2} sx={{ fontSize: '12px', color: COLORS.gray }}>
        <Typography component="span" sx={{ fontSize: '12px', color: COLORS.gray }}>
          {report.date}
        </Typography>
        <Typography component="span" sx={{ fontSize: '12px', color: COLORS.gray }}>
          {report.url}
        </Typography>
      </Stack>
    </Stack>
  );
};

const ScoreGradientBar = ({ score }) => {
  const clampedScore = Math.min(Math.max(score, SCORE_MIN), SCORE_MAX);
  const markerPct = ((clampedScore - SCORE_MIN) / (SCORE_MAX - SCORE_MIN)) * 100;

  return (
    <Box sx={{ mt: 'auto', pt: 3 }}>
      <Box sx={{ position: 'relative', pt: 1.5 }}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: `${markerPct}%`,
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderTop: '7px solid #111',
          }}
        />
        <Box
          sx={{
            height: 8,
            borderRadius: 4,
            background:
              'linear-gradient(90deg, #E85D4C 0%, #FF9800 20%, #F5D76E 38%, #7EC8E3 55%, #0097A7 72%, #4CAF50 100%)',
          }}
        />
      </Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
        {SCORE_AXIS_LABELS.map((label) => (
          <Typography key={label} variant="caption" sx={{ color: COLORS.gray, fontSize: '11px' }}>
            {label}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
};

const ScoreSection = ({ entity }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      pr: { md: 2 },
    }}
  >
    <OverlineLabel>MARMIN INVOICE CREDIT INDEX &middot; {entity.period}</OverlineLabel>

    <Typography
      sx={{
        fontFamily: SERIF_FONT,
        fontWeight: 700,
        color: COLORS.navy,
        fontSize: { xs: '28px', md: '36px' },
        lineHeight: 1.2,
        mt: 1.5,
      }}
    >
      {entity.name}
    </Typography>

    <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mt: 3 }} flexWrap="wrap">
      <Typography
        sx={{
          fontFamily: SERIF_FONT,
          fontWeight: 700,
          color: COLORS.navy,
          fontSize: { xs: '72px', sm: '96px', md: '112px' },
          lineHeight: 0.95,
        }}
      >
        {entity.score}
      </Typography>
      <Typography
        sx={{
          color: '#B0B0B0',
          fontSize: { xs: '20px', md: '26px' },
          fontWeight: 400,
        }}
      >
        / {entity.maxScore}
      </Typography>
      <Box
        sx={{
          bgcolor: COLORS.teal,
          color: '#fff',
          fontWeight: 700,
          fontSize: '12px',
          letterSpacing: 0.8,
          px: 1.5,
          py: 0.6,
          borderRadius: 0,
          alignSelf: 'center',
          ml: { xs: 0, sm: 0.5 },
        }}
      >
        {entity.rating}
      </Box>
    </Stack>

    <Typography sx={{ color: COLORS.gray, mt: 1.5, fontSize: '15px' }}>{entity.summary}</Typography>

    <ScoreGradientBar score={entity.score} />
  </Box>
);

const SubjectEntityCard = ({ entity }) => {
  const rows = Object.entries(entity.details);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: COLORS.border,
        bgcolor: COLORS.cardBg,
        borderRadius: '4px',
        p: { xs: 2.5, md: 3 },
        height: '100%',
      }}
    >
      <OverlineLabel sx={{ mb: 1.5 }}>SUBJECT ENTITY</OverlineLabel>
      <Stack spacing={0}>
        {rows.map(([label, value], index) => (
          <React.Fragment key={label}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ py: 1.1 }}>
              <Typography sx={{ color: COLORS.gray, flexShrink: 0, fontSize: '13px' }}>{label}</Typography>
              <Typography
                sx={{
                  color: COLORS.darkText,
                  fontWeight: 600,
                  textAlign: 'right',
                  fontSize: '13px',
                  lineHeight: 1.4,
                }}
              >
                {value}
              </Typography>
            </Stack>
            {index < rows.length - 1 && <Divider sx={{ borderColor: COLORS.border }} />}
          </React.Fragment>
        ))}
      </Stack>
    </Box>
  );
};

const DimensionCard = ({ dimension }) => {
  const TrendIcon = TREND_ICON[dimension.trend] ?? ArrowForwardIcon;
  const trendColor = TREND_COLOR[dimension.trend] ?? COLORS.teal;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: COLORS.border,
        borderRadius: '4px',
        p: 2.5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fff',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography sx={{ color: COLORS.gray, fontWeight: 600, fontSize: '12px' }}>
          {dimension.id} &middot; {dimension.weight}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <TrendIcon sx={{ fontSize: 16, color: trendColor }} />
          <Typography sx={{ color: trendColor, fontWeight: 700, fontSize: '15px' }}>{dimension.score}</Typography>
        </Stack>
      </Stack>

      <Typography
        sx={{
          color: COLORS.darkText,
          fontWeight: 700,
          mt: 1.25,
          mb: 2.5,
          fontSize: '15px',
          flexGrow: 1,
        }}
      >
        {dimension.title}
      </Typography>

      <LinearProgress
        variant="determinate"
        value={dimension.score}
        sx={{
          height: 5,
          borderRadius: 1,
          backgroundColor: alpha(dimension.color, 0.12),
          '& .MuiLinearProgress-bar': {
            backgroundColor: dimension.color,
            borderRadius: 1,
          },
        }}
      />
    </Box>
  );
};

const ComponentScoresSection = ({ dimensions }) => (
  <Box sx={{ mt: 5 }}>
    <OverlineLabel sx={{ mb: 2.5 }}>COMPONENT SCORES &middot; 8 DIMENSIONS</OverlineLabel>
    <Grid container spacing={2}>
      {dimensions.map((dimension) => (
        <Grid key={dimension.id} size={{ xs: 12, sm: 6 }}>
          <DimensionCard dimension={dimension} />
        </Grid>
      ))}
    </Grid>
  </Box>
);

const MiciScoreCard = ({ data }) => {
  if (!data?.report || !data?.entity || !data?.dimensions?.length) {
    return null;
  }

  const { entity, dimensions, report } = data;

  return (
    <Card
      elevation={0}
      sx={{
        bgcolor: '#fff',
        border: '1px solid',
        borderColor: COLORS.border,
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        p: { xs: 2.5, md: 4 },
      }}
    >
      <ReportHeader report={report} />

      <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
        <Grid size={{ xs: 12, md: 7 }}>
          <ScoreSection entity={entity} />
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <SubjectEntityCard entity={entity} />
        </Grid>
      </Grid>

      <ComponentScoresSection dimensions={dimensions} />
    </Card>
  );
};

export default MiciScoreCard;
